import React, { useState, useEffect } from 'react';
import {
    Grid,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    DialogContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { gpsAlertsApi, GpsAlert } from '../services/gpsAlertsApi';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

const GpsAlerts: React.FC = () => {
    const { t } = useLanguage();
    
    const [alerts, setAlerts] = useState<GpsAlert[]>([]);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingAlert, setEditingAlert] = useState<GpsAlert | null>(null);
    const formatDateTimeForInput = (d: any) => {
        if (!d) return '';
        const date = new Date(d);
        return isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 16);
    };

    const [formData, setFormData] = useState<Partial<GpsAlert>>({
        vehIdf: 0,
        alertType: 'speeding',
        alertMessage: '',
        timestamp: new Date(),
        status: 'active'
    });

    const loadData = async () => {
        try {
            const [alertsRes, vehiclesRes] = await Promise.all([
                gpsAlertsApi.getAlerts(),
                vehiclesApi.getAll()
            ]);

            if (vehiclesRes.success) {
                setVehicles(vehiclesRes.data);
            }
            setAlerts(alertsRes);
        } catch (error) {
            console.error('Failed to load data:', error);
            blobticMessage.error(t('common.messages.error'), 'Failed to load data');
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenDialog = (alert?: GpsAlert) => {
        if (alert) {
            setEditingAlert(alert);
            setFormData({
                ...alert
            });
        } else {
            setEditingAlert(null);
            setFormData({
                vehIdf: vehicles.length > 0 ? vehicles[0].id : 0,
                alertType: 'speeding',
                alertMessage: '',
                timestamp: new Date(),
                status: 'active'
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            // Clean up empty 0 IDs
            const submitData = { ...formData };
            if (submitData.vehIdf === 0) submitData.vehIdf = undefined;

            if (editingAlert) {
                await gpsAlertsApi.updateAlert(editingAlert.id, submitData);
            } else {
                await gpsAlertsApi.createAlert(submitData);
            }

            blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
            setIsDialogOpen(false);
            loadData();
        } catch (error) {
            console.error('Error saving alert:', error);
            blobticMessage.error(t('common.messages.error'), t('common.messages.error'));
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('common.actions.deleteConfirm'))) return;
        try {
            await gpsAlertsApi.deleteAlert(id);
            blobticMessage.success(t('common.messages.success'), t('common.messages.deleted'));
            loadData();
        } catch (error) {
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.error') || 'Unknown error');
        }
    };

    const columns = [
        { id: 'vehRegistration', label: t('parc.gpsTracking.vehicle') || 'Vehicle', minWidth: 120 },
        { id: 'alertType', label: t('parc.gpsalerts.type') || 'Type', minWidth: 120, format: (val: string) => t(`parc.gpsalerts.type.${val}`) || val },
        { id: 'alertMessage', label: t('parc.gpsalerts.message') || 'Message', minWidth: 200 },
        { id: 'timestamp', label: t('parc.repairs.date') || 'Date', minWidth: 100, format: (value: Date | string) => value ? new Date(value).toLocaleString() : '' },
        { id: 'status', label: t('parc.gpsalerts.status') || 'Status', minWidth: 100, format: (val: string) => t(`parc.gpsalerts.status.${val}`) || val }
    ];

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
        >
            {t('parc.gpsalerts.newAlert')}
        </Button>
    );

    return (
        <GenericPage title={t('parc.gpsalerts.title') || 'Alertes GPS'} additionalActions={additionalActions} footerEnabled={false} disableScroll>
            <GenericTable
                columns={columns}
                data={alerts}
                onEdit={handleOpenDialog}
                onDelete={(a: any) => handleDelete(a.id)}
            />

            <RootDialogPage
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={editingAlert ? t('parc.gpsalerts.editAlert') : t('parc.gpsalerts.newAlert')}
                onAction={handleSave}
            >
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.gpsTracking.vehicle')}</InputLabel>
                                <Select
                                    value={formData.vehIdf || 0}
                                    label={t('parc.gpsTracking.vehicle')}
                                    onChange={(e) => setFormData({ ...formData, vehIdf: Number(e.target.value) })}
                                >
                                    <MenuItem value={0}>{t('parc.fuel.none')}</MenuItem>
                                    {vehicles.map((v) => (
                                        <MenuItem key={v.id} value={v.id}>
                                            {v.registrationNumber} - {v.brand}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.gpsalerts.type')}</InputLabel>
                                <Select
                                    value={formData.alertType || 'speeding'}
                                    label={t('parc.gpsalerts.type')}
                                    onChange={(e) => setFormData({ ...formData, alertType: e.target.value })}
                                >
                                    <MenuItem value="speeding">{t('parc.gpsalerts.type.speeding')}</MenuItem>
                                    <MenuItem value="geofence">{t('parc.gpsalerts.type.geofence')}</MenuItem>
                                    <MenuItem value="maintenance">{t('parc.gpsalerts.type.maintenance')}</MenuItem>
                                    <MenuItem value="system">{t('parc.gpsalerts.type.system')}</MenuItem>
                                    <MenuItem value="other">{t('parc.gpsalerts.type.other')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.gpsalerts.status')}</InputLabel>
                                <Select
                                    value={formData.status || 'active'}
                                    label={t('parc.gpsalerts.status')}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <MenuItem value="active">{t('parc.gpsalerts.status.active')}</MenuItem>
                                    <MenuItem value="resolved">{t('parc.gpsalerts.status.resolved')}</MenuItem>
                                    <MenuItem value="ignored">{t('parc.gpsalerts.status.ignored')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="datetime-local"
                                label={t('parc.gpsalerts.date')}
                                InputLabelProps={{ shrink: true }}
                                value={formatDateTimeForInput(formData.timestamp)}
                                onChange={(e) => setFormData({ ...formData, timestamp: new Date(e.target.value) })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label={t('parc.gpsalerts.message')}
                                value={formData.alertMessage || ''}
                                onChange={(e) => setFormData({ ...formData, alertMessage: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </RootDialogPage>
        </GenericPage>
    );
};

export default GpsAlerts;
